import type { Collectible as GameCollectible, Position } from '@/types/game';
import type { Collectible as PuzzleCollectible } from '@/types/puzzle';
import {
  findTraversalRouteToExit,
  isCollectibleTile,
  parseTraversalLevel,
  solveLevelWithCollectibles,
} from '@/game/levelTraversal';

export interface ResolvedCollectiblesResult {
  collectibles: GameCollectible[];
  usedFallback: boolean;
  reason: 'puzzle' | 'fallback-empty' | 'fallback-unsolvable' | 'fallback-invalid';
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function hashGrid(grid: string[]): number {
  let hash = 17;
  for (const row of grid) {
    for (let i = 0; i < row.length; i++) {
      hash = (hash * 31 + row.charCodeAt(i)) | 0;
    }
    hash = (hash * 31 + 7) | 0;
  }
  return Math.abs(hash);
}

function inBounds(grid: string[], [x, y]: Position): boolean {
  if (y < 0 || y >= grid.length) return false;
  const row = grid[y];
  return x >= 0 && x < row.length;
}

function normalizePuzzleCollectibles(
  grid: string[],
  collectibles: PuzzleCollectible[],
): PuzzleCollectible[] {
  const seen = new Set<string>();
  const normalized: PuzzleCollectible[] = [];

  for (const collectible of collectibles) {
    const pos = collectible.position;
    if (!inBounds(grid, pos)) continue;
    if (!isCollectibleTile(grid, pos)) continue;

    const key = `${pos[0]},${pos[1]}`;
    if (seen.has(key)) continue;
    seen.add(key);

    const type = collectible.type === 'gem' ? 'gem' : 'coin';
    const fallbackValue = type === 'gem' ? 5 : 1;
    const value = Number.isFinite(collectible.value) && collectible.value > 0 ? collectible.value : fallbackValue;
    const id = collectible.id?.trim() || `puzzle_${key}`;

    normalized.push({
      ...collectible,
      type,
      value,
      id,
      position: [pos[0], pos[1]],
    });
  }

  return normalized;
}

function uniquePositions(positions: Position[]): Position[] {
  const unique = new Map<string, Position>();
  for (const pos of positions) {
    unique.set(`${pos[0]},${pos[1]}`, [pos[0], pos[1]]);
  }
  return Array.from(unique.values());
}

function rotate<T>(values: T[], by: number): T[] {
  if (values.length <= 1) return values.slice();
  const offset = ((by % values.length) + values.length) % values.length;
  return values.slice(offset).concat(values.slice(0, offset));
}

function pickSpacedPositions(candidates: Position[], desiredCount: number, seed: number): Position[] {
  if (candidates.length === 0 || desiredCount <= 0) return [];
  if (desiredCount >= candidates.length) return candidates.slice();

  const rotated = rotate(candidates, seed);
  const selected: Position[] = [];
  const used = new Set<string>();
  const stride = rotated.length / (desiredCount + 1);

  for (let i = 1; i <= desiredCount; i++) {
    let index = Math.min(rotated.length - 1, Math.floor(i * stride));
    let attempts = 0;
    while (attempts < rotated.length) {
      const pos = rotated[index];
      const key = `${pos[0]},${pos[1]}`;
      if (!used.has(key)) {
        used.add(key);
        selected.push(pos);
        break;
      }
      index = (index + 1) % rotated.length;
      attempts += 1;
    }
  }

  return selected;
}

function estimateCollectibleCount(grid: string[]): number {
  let openTiles = 0;
  grid.forEach((row, y) => {
    row.split('').forEach((_, x) => {
      if (isCollectibleTile(grid, [x, y])) openTiles += 1;
    });
  });

  return clamp(Math.round(openTiles / 9), 1, 5);
}

function buildGeneratedCollectibles(
  grid: string[],
  levelIndex: number,
): PuzzleCollectible[] {
  const level = parseTraversalLevel(grid);
  const route = findTraversalRouteToExit(grid);
  const routeTiles = route
    ? uniquePositions(route.traversed.filter((pos) => isCollectibleTile(grid, pos)))
    : [];

  const fallbackTiles = routeTiles.length > 0
    ? routeTiles
    : (() => {
        const allOpenTiles: Position[] = [];
        grid.forEach((row, y) => {
          row.split('').forEach((_, x) => {
            const pos: Position = [x, y];
            if (isCollectibleTile(grid, pos)) allOpenTiles.push(pos);
          });
        });
        return uniquePositions(allOpenTiles);
      })();

  const seed = hashGrid(grid) + levelIndex * 101;
  const desiredCount = clamp(
    Math.min(estimateCollectibleCount(grid), Math.max(1, fallbackTiles.length)),
    1,
    5,
  );
  let selected = pickSpacedPositions(fallbackTiles, desiredCount, seed);

  // Absolute fallback: use the exit tile if no normal placement exists.
  if (selected.length === 0) {
    selected = [[level.end[0], level.end[1]]];
  }

  return selected.map((position, index) => {
    const isGem = selected.length > 2 && (index + seed) % 4 === 0;
    return {
      id: `auto_${levelIndex}_${position[0]}_${position[1]}_${index}`,
      position: [position[0], position[1]],
      type: isGem ? 'gem' : 'coin',
      value: isGem ? 5 : 1,
    };
  });
}

function toGameCollectibles(
  collectibles: PuzzleCollectible[],
  restartCounter: number,
): GameCollectible[] {
  return collectibles.map((collectible, index) => ({
    id: `${collectible.id}-${restartCounter}-${index}`,
    type: collectible.type,
    position: [collectible.position[0], collectible.position[1]],
    value: collectible.value,
    isCollected: false,
  }));
}

export function resolveLevelCollectibles(
  grid: string[],
  puzzleCollectibles: PuzzleCollectible[],
  levelIndex: number,
  restartCounter: number,
): ResolvedCollectiblesResult {
  const normalizedPuzzleCollectibles = normalizePuzzleCollectibles(grid, puzzleCollectibles);

  if (normalizedPuzzleCollectibles.length > 0) {
    const solveWithPuzzleCollectibles = solveLevelWithCollectibles(
      grid,
      normalizedPuzzleCollectibles.map((c) => c.position),
    );

    if (solveWithPuzzleCollectibles.solvable) {
      return {
        collectibles: toGameCollectibles(normalizedPuzzleCollectibles, restartCounter),
        usedFallback: false,
        reason: 'puzzle',
      };
    }
  }

  const generated = buildGeneratedCollectibles(grid, levelIndex);
  const solveWithGenerated = solveLevelWithCollectibles(
    grid,
    generated.map((collectible) => collectible.position),
  );

  if (solveWithGenerated.solvable && generated.length > 0) {
    return {
      collectibles: toGameCollectibles(generated, restartCounter),
      usedFallback: true,
      reason: normalizedPuzzleCollectibles.length > 0 ? 'fallback-unsolvable' : 'fallback-empty',
    };
  }

  // Last-resort defensive fallback: single collectible on exit.
  const level = parseTraversalLevel(grid);
  const emergencyCollectible: PuzzleCollectible = {
    id: `auto_${levelIndex}_emergency_exit`,
    type: 'coin',
    value: 1,
    position: [level.end[0], level.end[1]],
  };

  return {
    collectibles: toGameCollectibles([emergencyCollectible], restartCounter),
    usedFallback: true,
    reason: 'fallback-invalid',
  };
}
